interface ToggleRowProps {
    /** When provided, renders the toggle inside a labeled row. Omit for a standalone toggle button. */
    label?: string
    checked: boolean
    onChange: (value: boolean) => void
    disabled?: boolean
    /** Optional trailing content rendered next to the label (e.g., a "Required" badge). */
    labelAdornment?: preact.ComponentChildren
    /** Overrides the switch's accessible name. Falls back to `label` when set. */
    ariaLabel?: string
    /** Id of the text that explains the switch, such as why it is disabled. */
    ariaDescribedBy?: string
    /** Visual size variant. Defaults to 'default'. */
    size?: 'default' | 'sm'
}

export function ToggleRow({ label, checked, onChange, disabled, labelAdornment, ariaLabel, ariaDescribedBy, size = 'default' }: ToggleRowProps) {
    const toggle = (
        <button
            type="button"
            role="switch"
            aria-checked={checked}
            aria-label={ariaLabel ?? label}
            aria-describedby={ariaDescribedBy}
            disabled={disabled}
            onClick={() => onChange(!checked)}
            className={`
                relative inline-flex flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out
                ${size === 'sm' ? 'h-5 w-9' : 'h-6 w-11'}
                ${checked ? 'bg-primary-600' : 'bg-slate-200 dark:bg-slate-600'}
                ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}
            `}
        >
            <span
                className={`
                    pointer-events-none inline-block rounded-full bg-white shadow ring-0 transition-transform duration-200 ease-in-out
                    ${size === 'sm' ? 'h-4 w-4' : 'h-5 w-5'}
                    ${size === 'sm'
                        ? (checked ? 'translate-x-4' : 'translate-x-0')
                        : (checked ? 'translate-x-5' : 'translate-x-0')
                    }
                `}
            />
        </button>
    )

    if (!label) return toggle

    return (
        <label className={`flex items-center justify-between py-2 ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
            <span className="flex items-baseline gap-2">
                <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{label}</span>
                {labelAdornment}
            </span>
            {toggle}
        </label>
    )
}
